import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table, Button, Switch, Tag, Modal, Form, Input, Select,
  Space, Popconfirm, message, Divider, Collapse, TimePicker, Checkbox,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
} from "@ant-design/icons";
import {
  fetchBasicReplies, createBasicReply, updateBasicReply,
  deleteBasicReply, toggleBasicReply,
} from "../../../../../core/data/redux/slices/WabaAutomationSlice";
import dayjs from "dayjs";

const { Option } = Select;
const { Panel } = Collapse;

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const RESPONSE_TYPES = [
  { value: "text",     label: "Text" },
  { value: "image",    label: "Image" },
  { value: "video",    label: "Video" },
  { value: "audio",    label: "Audio" },
  { value: "document", label: "Document" },
];

const emptyResponse = () => ({ type: "text", text: "", mediaUrl: "", caption: "" });

const BasicReplies = () => {
  const dispatch = useDispatch();
  const { basicReplies, loading } = useSelector((s) => s.wabaAutomation);

  const [modalOpen, setModalOpen]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form]                      = Form.useForm();
  const [responses, setResponses]   = useState([emptyResponse()]);
  const [days, setDays]             = useState(["Mon","Tue","Wed","Thu","Fri"]);
  const [search, setSearch]         = useState("");

  useEffect(() => { dispatch(fetchBasicReplies()); }, [dispatch]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setResponses([emptyResponse()]);
    setDays(["Mon","Tue","Wed","Thu","Fri"]);
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      trigger: record.trigger,
      matchCriteria: record.matchCriteria,
      bhEnabled: record.businessHours?.enabled,
      bhTimezone: record.businessHours?.timezone || "UTC",
      bhStart: record.businessHours?.startTime ? dayjs(record.businessHours.startTime, "HH:mm") : null,
      bhEnd:   record.businessHours?.endTime   ? dayjs(record.businessHours.endTime,   "HH:mm") : null,
    });
    setResponses(record.responses?.length ? record.responses : [emptyResponse()]);
    setDays(record.businessHours?.days || ["Mon","Tue","Wed","Thu","Fri"]);
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name:          values.name,
        trigger:       values.trigger,
        matchCriteria: values.matchCriteria || "contains",
        responses,
        businessHours: {
          enabled:   !!values.bhEnabled,
          timezone:  values.bhTimezone || "UTC",
          days,
          startTime: values.bhStart ? values.bhStart.format("HH:mm") : "09:00",
          endTime:   values.bhEnd   ? values.bhEnd.format("HH:mm")   : "18:00",
        },
      };

      if (editing) {
        await dispatch(updateBasicReply({ id: editing._id, ...payload })).unwrap();
        message.success("Updated");
      } else {
        await dispatch(createBasicReply(payload)).unwrap();
        message.success("Created");
      }
      setModalOpen(false);
    } catch (err) {
      if (err?.message) message.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    await dispatch(deleteBasicReply(id)).unwrap();
    message.success("Deleted");
  };

  const handleToggle = async (id) => {
    await dispatch(toggleBasicReply(id)).unwrap();
  };

  const updateResponse = (index, field, value) => {
    setResponses((prev) => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const filtered = basicReplies.filter((r) =>
    r.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.trigger?.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    { title: "Name", dataIndex: "name", key: "name", render: (v) => <strong>{v}</strong> },
    {
      title: "Trigger", dataIndex: "trigger", key: "trigger",
      render: (v, r) => (
        <span>
          <Tag color="blue">{r.matchCriteria === "exact" ? "Exact" : "Contains"}</Tag>
          <code style={{ fontSize: 12 }}>{v}</code>
        </span>
      ),
    },
    {
      title: "Responses", dataIndex: "responses", key: "responses",
      render: (v) => <Tag>{v?.length || 0} response{v?.length !== 1 ? "s" : ""}</Tag>,
    },
    {
      title: "Business Hours", key: "bh",
      render: (_, r) => r.businessHours?.enabled
        ? <Tag color="green">{r.businessHours.startTime} – {r.businessHours.endTime}</Tag>
        : <Tag color="default">24/7</Tag>,
    },
    {
      title: "Active", key: "active",
      render: (_, r) => <Switch checked={r.isActive} onChange={() => handleToggle(r._id)} size="small" />,
    },
    {
      title: "Actions", key: "actions",
      render: (_, r) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(r)} />
          <Popconfirm title="Delete this reply?" onConfirm={() => handleDelete(r._id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0 }}>Basic Replies</h3>
          <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>Auto-reply when a keyword is received</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Create Reply</Button>
      </div>

      <Input
        prefix={<SearchOutlined />}
        placeholder="Search by name or trigger"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ maxWidth: 320, marginBottom: 16 }}
      />

      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="_id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editing ? "Edit Basic Reply" : "Create Basic Reply"}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        width={640}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Greeting reply" />
          </Form.Item>

          <Space.Compact style={{ width: "100%" }}>
            <Form.Item name="matchCriteria" label="Match" style={{ width: 140 }}>
              <Select defaultValue="contains">
                <Option value="contains">Contains</Option>
                <Option value="exact">Exact</Option>
              </Select>
            </Form.Item>
            <Form.Item name="trigger" label="Trigger keyword" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Input placeholder="e.g. hello" />
            </Form.Item>
          </Space.Compact>

          <Divider orientation="left" plain style={{ fontSize: 13 }}>Responses (sent in order)</Divider>

          {responses.map((resp, i) => (
            <div key={i} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 12, color: "#6b7280" }}>#{i + 1}</span>
                <Select
                  value={resp.type}
                  onChange={(v) => updateResponse(i, "type", v)}
                  size="small"
                  style={{ width: 130 }}
                  options={RESPONSE_TYPES}
                />
                {responses.length > 1 && (
                  <Button size="small" danger onClick={() => setResponses((p) => p.filter((_, idx) => idx !== i))}>
                    Remove
                  </Button>
                )}
              </div>
              {resp.type === "text" ? (
                <Input.TextArea
                  value={resp.text}
                  onChange={(e) => updateResponse(i, "text", e.target.value)}
                  rows={2}
                  placeholder="Enter message text"
                />
              ) : (
                <>
                  <Input
                    value={resp.mediaUrl}
                    onChange={(e) => updateResponse(i, "mediaUrl", e.target.value)}
                    placeholder="Media URL"
                    style={{ marginBottom: 6 }}
                  />
                  <Input
                    value={resp.caption}
                    onChange={(e) => updateResponse(i, "caption", e.target.value)}
                    placeholder="Caption (optional)"
                  />
                </>
              )}
            </div>
          ))}

          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setResponses((p) => [...p, emptyResponse()])}
            style={{ marginBottom: 16 }}
          >
            Add Response
          </Button>

          <Collapse ghost>
            <Panel header="Business Hours (optional)" key="bh">
              <Form.Item name="bhEnabled" valuePropName="checked">
                <Checkbox>Enable business hours restriction</Checkbox>
              </Form.Item>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
                {DAYS.map((d) => (
                  <Checkbox
                    key={d}
                    checked={days.includes(d)}
                    onChange={(e) => setDays((prev) => e.target.checked ? [...prev, d] : prev.filter((x) => x !== d))}
                  >
                    {d}
                  </Checkbox>
                ))}
              </div>
              <Space>
                <Form.Item name="bhStart" label="From" style={{ marginBottom: 0 }}>
                  <TimePicker format="HH:mm" />
                </Form.Item>
                <Form.Item name="bhEnd" label="To" style={{ marginBottom: 0 }}>
                  <TimePicker format="HH:mm" />
                </Form.Item>
              </Space>
            </Panel>
          </Collapse>
        </Form>
      </Modal>
    </div>
  );
};

export default BasicReplies;
